<script setup lang="ts">
import { PropType, ref } from "vue";
import { Participant, Project, TranslatedProduct } from "../../../types/PersologOnlineTypes";
import EditableBenchmarkDiagram from "../job-match/EditableBenchmarkDiagram.vue";
import { CalculatorAPI, DirectInputAPI, ProfileAdministrationAPI } from "../../../api";
import { makeAssociatedDirectInput } from "../../../utils/JobMatchUtils.ts";
import { BENCHMARK_BASIC, createParticipantAndLogActivity } from "../../../utils/ProjectUtils.ts";

const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        default: null,
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    dominantRange: {
        type: Array as PropType<number[]>,
        default: () => [12.5, 87.5],
    },
    initiativeRange: {
        type: Array as PropType<number[]>,
        default: () => [12.5, 87.5],
    },
    steadyRange: {
        type: Array as PropType<number[]>,
        default: () => [12.5, 87.5],
    },
    cautiousRange: {
        type: Array as PropType<number[]>,
        default: () => [12.5, 87.5],
    },
});

const emit = defineEmits(["valid", "update:visible"]);

const participants = ref<Participant[]>(props.participants);

/**
 * Creates a new project in the backend.
 * Participants, Direct links, roles and departments are added if they are available.
 * It emits the update:visible event to notify the parent component that the project has been created
 * emits e.g.: this -> ConfigureProject -> ProjectView
 * @emits update:visible
 */
async function createProject() {
    const createdProject: Project = await ProfileAdministrationAPI.createProject(props.project as Project);

    if (participants.value && participants.value.length > 0) {
        const participantData = {
            participants: participants.value.map((p) => ({
                ...p,
            })),
        };
        await createParticipantAndLogActivity(createdProject.id as number, participantData.participants);
    }

    const associatedDirectInput = await DirectInputAPI.saveDirectInputGroup(
        createdProject.trainer ?? "",
        makeAssociatedDirectInput(
            createdProject.id!,
            BENCHMARK_BASIC,
            props.dominantRange,
            props.initiativeRange,
            props.steadyRange,
            props.cautiousRange,
        ),
    );
    await CalculatorAPI.calculateAndPersistDirectLinkGroup(createdProject.trainer ?? "", associatedDirectInput);
    emit("update:visible", createdProject);
}

const validate = () => {
    emit("valid", true);
    createProject();
    return true;
};
defineExpose({ validate });
</script>

<template>
    <div class="card flex-row step-content">
        <h2 v-if="props.product">{{ props.product.productName }}</h2>
        <EditableBenchmarkDiagram
            :editable="false"
            :dominant-range="props.dominantRange"
            :initiative-range="props.initiativeRange"
            :steady-range="props.steadyRange"
            :cautious-range="props.cautiousRange"
            :language="props.project?.language || 'en'"
        ></EditableBenchmarkDiagram>
    </div>
</template>

<style scoped>
/* CSS */
</style>
